import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useEmailConnections, useDisconnectProvider } from '@/hooks/use-eirsaliye';
import { eirsaliyeService } from '@/lib/eirsaliye-service';
import { useToast } from '@/hooks/use-toast';
import { Mail, Link2, Unlink, RefreshCw, CheckCircle, Clock } from 'lucide-react';

export function EmailConnectionCard() {
  const { data: status, isLoading, refetch } = useEmailConnections();
  const disconnectMutation = useDisconnectProvider();
  const { toast } = useToast();
  const [connecting, setConnecting] = useState<string | null>(null);

  // Listen for OAuth popup completion
  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.data?.type === 'email-oauth-result') {
      setConnecting(null);
      if (event.data.success) {
        toast({
          title: 'Hesap Baglandi',
          description: `${event.data.detail} basariyla baglandi.`,
        });
        refetch();
      } else {
        toast({
          title: 'Baglanti Hatasi',
          description: event.data.detail,
          variant: 'destructive',
        });
      }
    }
  }, [toast, refetch]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  const handleConnect = async (provider: 'gmail' | 'outlook') => {
    setConnecting(provider);
    try {
      const authUrl = await eirsaliyeService.initiateOAuth(provider);
      // Open OAuth popup — user logs in with their email/password on Google/Microsoft page
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      window.open(
        authUrl,
        'email-oauth',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`,
      );
    } catch (error: any) {
      setConnecting(null);
      toast({
        title: 'Baglanti Hatasi',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDisconnect = (provider: 'gmail' | 'outlook') => {
    disconnectMutation.mutate(provider);
  };

  const renderConnectionRow = (
    provider: 'gmail' | 'outlook',
    label: string,
    connection: any | null,
  ) => {
    const isConnecting = connecting === provider;

    if (connection) {
      return (
        <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <div>
              <div className="font-medium text-sm">{label}</div>
              <div className="text-xs text-muted-foreground">{connection.email_address}</div>
              {connection.last_sync_at && (
                <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                  <Clock className="w-3 h-3" />
                  Son sync: {new Date(connection.last_sync_at).toLocaleString('tr-TR')}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {connection.last_sync_status && (
              <div className="flex flex-col items-end gap-0.5">
                <Badge variant={connection.last_sync_status === 'success' ? 'default' : 'destructive'} className="text-xs">
                  {connection.last_sync_status === 'success' ? 'Basarili' : 'Hata'}
                </Badge>
                {connection.last_sync_status !== 'success' && connection.last_sync_error && (
                  <span className="text-[10px] text-destructive max-w-[200px] truncate" title={connection.last_sync_error}>
                    {connection.last_sync_error}
                  </span>
                )}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDisconnect(provider)}
              disabled={disconnectMutation.isPending}
            >
              <Unlink className="w-3 h-3 mr-1" />
              Kes
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between p-3 rounded-lg border border-dashed">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <Mail className="w-4 h-4 text-muted-foreground" />
          </div>
          <div>
            <div className="font-medium text-sm">{label}</div>
            <div className="text-xs text-muted-foreground">Bagli degil</div>
          </div>
        </div>
        <Button
          size="sm"
          onClick={() => handleConnect(provider)}
          disabled={isConnecting}
        >
          {isConnecting ? (
            <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Baglaniyor...</>
          ) : (
            <><Link2 className="w-3 h-3 mr-1" />Bagla</>
          )}
        </Button>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Mail className="w-5 h-5" />
          Email Baglantisi
        </CardTitle>
        <CardDescription>
          e-Irsaliye emaillerini okumak icin Gmail veya Outlook hesabinizi baglayin.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {renderConnectionRow('gmail', 'Gmail', status?.gmail)}
            {renderConnectionRow('outlook', 'Outlook', status?.outlook)}
          </>
        )}
      </CardContent>
    </Card>
  );
}
